import type { FlagDTO, SnapshotDTO } from "../../shared/protocol/index.js";
import type { Renderer } from "./Renderer.js";
import type { FogView } from "./types.js";
import { fogVisionIncludes, isPointVisible } from "./fog.js";

/** CTF flags: a pennant on a pole in the team's color. Dropped flags pulse.
 *  When one tank carries several flags, their pennants stack up a taller pole
 *  (lowest team index at the bottom) so every held flag stays visible. */
export function drawFlags(rd: Renderer, interp: SnapshotDTO, nowMs: number, fog: FogView | null): void {
  if (interp.flags.length === 0) return;
  const teamColor = new Map(rd.spawnZones.map((z) => [z.team, z.color]));
  const stackIndex = flagStackIndex(interp.flags);
  for (const fl of interp.flags) {
    if (fog && !isPointVisible(fl.x, fl.y, fog)) continue;
    drawFlag(rd, fl, teamColor.get(fl.team) ?? "#888888", stackIndex.get(fl) ?? 0, nowMs, 1);
  }
}

/**
 * Flags don't grant vision. Instead, when fogFlagVision allows it, draw flags
 * that fall outside the team's sight faintly on top of the fog so players can
 * still spot them without revealing the surrounding maze.
 */
export function drawFlagMarkers(rd: Renderer, interp: SnapshotDTO, nowMs: number, fog: FogView): void {
  if (rd.fogFlagVision === "off" || interp.flags.length === 0) return;
  const teamColor = new Map(rd.spawnZones.map((z) => [z.team, z.color]));
  const stackIndex = flagStackIndex(interp.flags);
  for (const fl of interp.flags) {
    if (isPointVisible(fl.x, fl.y, fog)) continue; // already drawn crisply in sight
    // A carried flag must not be trackable across the fog (only via line of sight).
    if (rd.fogHideCarriedFlag && fl.state === "carried") continue;
    if (!fogVisionIncludes(fl.team, fog.local.team, rd.fogFlagVision)) continue;
    drawFlag(
      rd,
      fl,
      teamColor.get(fl.team) ?? "#888888",
      stackIndex.get(fl) ?? 0,
      nowMs,
      0.75,
      "rgba(255,255,255,0.9)",
    );
  }
}

/** Stack order for carried flags: index within their carrier's held set. */
function flagStackIndex(flags: FlagDTO[]): Map<FlagDTO, number> {
  const stackIndex = new Map<FlagDTO, number>();
  const byCarrier = new Map<number, FlagDTO[]>();
  for (const fl of flags) {
    if (fl.state !== "carried" || fl.carrier === 255) continue;
    const list = byCarrier.get(fl.carrier) ?? [];
    list.push(fl);
    byCarrier.set(fl.carrier, list);
  }
  for (const list of byCarrier.values()) {
    list.sort((a, b) => a.team - b.team);
    list.forEach((fl, i) => stackIndex.set(fl, i));
  }
  return stackIndex;
}

/** Draw a single CTF flag (pennant on a pole). `alpha` scales opacity so the
 *  same drawing serves both crisp in-sight flags and faint fog markers.
 *  `outline`, when set, draws a contrasting halo so the flag pops over fog. */
function drawFlag(
  rd: Renderer,
  fl: FlagDTO,
  color: string,
  tier: number,
  nowMs: number,
  alpha: number,
  outline?: string,
): void {
  const { ctx } = rd;
  const PENNANT_GAP = 9; // vertical spacing between stacked pennants
  const bob = fl.state === "carried" ? 0 : Math.sin(nowMs / 300) * 1.5;
  const peak = 18 + tier * PENNANT_GAP; // pole height for this pennant's tier
  ctx.save();
  ctx.translate(fl.x, fl.y + bob);
  ctx.lineJoin = "round";
  const pulse = fl.state === "dropped" ? 0.55 + 0.25 * Math.abs(Math.sin(nowMs / 250)) : 1;
  ctx.globalAlpha = pulse * alpha;
  const polePath = () => {
    ctx.beginPath();
    ctx.moveTo(0, 4);
    ctx.lineTo(0, -peak);
  };
  const pennantPath = () => {
    ctx.beginPath();
    ctx.moveTo(0, -peak);
    ctx.lineTo(13, -peak + 4.5);
    ctx.lineTo(0, -peak + 9);
    ctx.closePath();
  };
  // Contrasting outline drawn underneath so the flag stands out against fog.
  if (outline) {
    ctx.strokeStyle = outline;
    ctx.lineWidth = 5;
    polePath();
    ctx.stroke();
    pennantPath();
    ctx.stroke();
  }
  // Pole (tall enough to reach this tier's pennant).
  ctx.strokeStyle = "#352f25";
  ctx.lineWidth = 2;
  polePath();
  ctx.stroke();
  // Pennant at this tier.
  ctx.fillStyle = color;
  ctx.strokeStyle = "rgba(0,0,0,0.5)";
  ctx.lineWidth = 1;
  pennantPath();
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}
